import { tokens } from './tokens';

const BASE = '/api';

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: { message?: string | string[]; error?: string } = {},
  ) {
    const msg = Array.isArray(body.message) ? body.message.join(', ') : body.message;
    super(msg || body.error || `Request failed (${status})`);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  auth?: boolean;
  /**
   * Extra request headers merged in AFTER content-type + authorization, so
   * callers can override either if they need to. Used by the free-tool pages
   * to attach `X-Turnstile-Token` when Cloudflare Turnstile is wired up.
   * Undefined / null values are dropped — convenient for conditional headers
   * (`{ 'X-Turnstile-Token': token ?? undefined }`).
   */
  headers?: Record<string, string | undefined | null>;
}

async function send(path: string, opts: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (opts.auth !== false && tokens.access) headers['authorization'] = `Bearer ${tokens.access}`;
  if (opts.headers) {
    for (const [k, v] of Object.entries(opts.headers)) {
      if (v !== undefined && v !== null) headers[k] = v;
    }
  }

  return fetch(BASE + path, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
}

let refreshing: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  if (!tokens.refresh) return false;
  if (!refreshing) {
    refreshing = (async () => {
      const res = await send('/auth/refresh', {
        method: 'POST',
        body: { refreshToken: tokens.refresh },
        auth: false,
      });
      if (!res.ok) {
        tokens.clear();
        return false;
      }
      const data = (await res.json()) as { accessToken: string; refreshToken: string };
      tokens.set(data.accessToken, data.refreshToken);
      return true;
    })().finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
}

/** Typed JSON request with transparent one-shot token refresh on 401. */
export async function api<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  let res = await send(path, opts);

  if (res.status === 401 && opts.auth !== false && tokens.refresh) {
    if (await refreshTokens()) res = await send(path, opts);
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/** Fetch an authenticated file response and trigger a browser download. */
export async function download(path: string, filename: string): Promise<void> {
  let res = await send(path, {});
  if (res.status === 401 && tokens.refresh && (await refreshTokens())) {
    res = await send(path, {});
  }
  if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => ({})));
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
